import { proto, CellProps } from "ali-react-table";
import React, { useEffect, useRef, useState } from "react";
import { Bubble } from "tea-component";
import { HeatCellDataStruct, HeatMapTableColumn } from "@/pages/dashboard/components/HeatMapTable";

/** 同一时刻只允许一个热力图浮层打开，快速划过多行时关闭上一个 */
let activeBubbleCloser: (() => void) | null = null;

/** 通过 React 状态关闭当前浮层，禁止直接操作 DOM（会与 portal 卸载冲突） */
export const closeActiveHeatMapBubble = (): void => {
  if (activeBubbleCloser) {
    activeBubbleCloser();
    activeBubbleCloser = null;
  }
};

interface IHeatMapBubbleCellProps {
  value: HeatCellDataStruct;
  overlayClassName?: string;
}

/**
 * 受控 Bubble：全局互斥 + 零延迟开关，避免快速 hover 多行时浮层堆叠残留
 */
const HeatMapBubbleCellInner: React.FC<IHeatMapBubbleCellProps> = ({
  value,
  overlayClassName,
}) => {
  const [visible, setVisible] = useState<boolean>(false);
  const instanceRef = useRef<{ close: () => void; }>();

  if (!instanceRef.current) {
    instanceRef.current = {
      close: () => {
        setVisible(false);
      },
    };
  }

  const handleVisibleChange = (nextVisible: boolean): void => {
    const { close } = instanceRef.current!;

    if (nextVisible) {
      if (activeBubbleCloser && activeBubbleCloser !== close) {
        activeBubbleCloser();
      }
      activeBubbleCloser = close;
    } else if (activeBubbleCloser === close) {
      activeBubbleCloser = null;
    }
    setVisible(nextVisible);
  };

  useEffect(() => {
    const { close } = instanceRef.current!;
    return () => {
      if (activeBubbleCloser === close) {
        activeBubbleCloser = null;
      }
      close();
    };
  }, []);

  return (
    <Bubble
      trigger="hover"
      openDelay={0}
      closeDelay={0}
      placementOffset={[110, -106]}
      className="Revenue-table-Hover_Bubble"
      overlayClassName={overlayClassName}
      closeOnScroll={true}
      destroyOnClose={true}
      {...value?.bubble}
      visible={visible}
      onVisibleChange={handleVisibleChange}
    >
      {value?.cellContent}
    </Bubble>
  );
};

/** retention avg 等：保留 Bubble 包裹以维持单元格 hover 边框，仅通过 visible=false 禁用浮层 */
const HeatMapBubbleCellDisabled: React.FC<IHeatMapBubbleCellProps> = ({
  value,
  overlayClassName,
}) => (
  <Bubble
    placementOffset={[110, -106]}
    className="Revenue-table-Hover_Bubble"
    overlayClassName={overlayClassName}
    closeOnScroll={true}
    {...value?.bubble}
    visible={false}
  >
    {value?.cellContent}
  </Bubble>
);

const HeatMapBubbleCell: React.FC<IHeatMapBubbleCellProps> = (props) => {
  if (props.value?.bubble?.visible === false) {
    return <HeatMapBubbleCellDisabled {...props} />;
  }
  return <HeatMapBubbleCellInner {...props} />;
};

export const creatHeatColumns = (columns: HeatMapTableColumn[] = [], className?: string): HeatMapTableColumn[] => {
  const heatColumns = proto.array<HeatMapTableColumn>({
    width: 126,
    align: "right",
    features: { sortable: true },
    render(value: HeatCellDataStruct): React.ReactNode {
      return (
        <HeatMapBubbleCell value={value} overlayClassName={className} />
      );
    },
    getCellProps(value: any): CellProps {
      return {
        style: {
          background: `rgba(55,106,213,${Number(value?.colorPercent)})`,
        },
        className: "HeatMapTable-cell"
      };
    }
  });
  return heatColumns(columns);
};
